import React from 'react';

/**
 * Search Page Messaging (Vertex): spell correction / ZSR / low-result.
 *
 * Config owns the copy, including how it looks — templates use inline markup
 * (<strong>...</strong>, <link>...</link>) around {token} placeholders, e.g.
 * "No exact match for <strong>{freeTextSearch}</strong>. Showing related matches below".
 * This file interpolates tokens and renders the markup; tokens themselves are plain
 * data (strings/numbers), never styling decisions.
 */

// reasonForExpansion (backend contract) -> config message key.
const REASON_TO_MESSAGE_KEY = {
  'Spell Correction': 'spellCorrection',
  ZSR: 'zsr',
  'Low Result': 'lowResult'
};

// reasonForExpansion (backend contract) -> Segment search_expansion_type values.
const REASON_TO_EXPANSION_TYPE = {
  'Spell Correction': 'spellcheck expansion',
  ZSR: 'zsr expansion',
  'Low Result': 'low results expansion'
};

/**
 * Segment property for Search PLP Screen Viewed / Product Viewed / Product Clicked.
 * Values: 'zsr expansion' | 'low results expansion' | 'spellcheck expansion' | 'spellcheck disabled'
 * Only set on /search or /find; empty string when none apply.
 */
export function getSearchExpansionType(productsAdditionalInfo) {
  if (typeof window === 'undefined') {
    return '';
  }
  const path = window.location.pathname || '';
  if (!path.includes('/search') && !path.includes('/find')) {
    return '';
  }

  const params = new URLSearchParams(window.location.search || '');
  if (params.get('spellCheckEnable') === 'false') {
    return 'spellcheck disabled';
  }

  const info =
    productsAdditionalInfo ||
    window?.store?.getState?.()?.productsAdditionalInfo ||
    // eslint-disable-next-line no-underscore-dangle
    window?.__PRELOADED_STATE__?.productsAdditionalInfo ||
    {};
  const { isQueryExpanded = false, reasonForExpansion = '', responseSource = '' } = info;

  if (String(responseSource).toLowerCase() !== 'vertex' || !isQueryExpanded) {
    return '';
  }
  return REASON_TO_EXPANSION_TYPE[reasonForExpansion] || '';
}

// Looks up a message template for a platform + key, honoring the enabled/store gates.
function resolveMessage(searchMessaging, storeName, platform, messageKey) {
  if (!searchMessaging || searchMessaging.enabled !== true) {
    return '';
  }
  const store = storeName || 'ajio';
  if (searchMessaging.stores && searchMessaging.stores[store] !== true) {
    return '';
  }
  const messagesForPlatform = (searchMessaging.messages || {})[platform] || {};
  return (messageKey && messagesForPlatform[messageKey]) || '';
}

/** Template for a reasonForExpansion (ZSR / Spell Correction / Low Result's in-grid divider). */
export function getSearchMessage(
  searchMessaging,
  storeName,
  reasonForExpansion,
  platform = 'desktop'
) {
  return resolveMessage(
    searchMessaging,
    storeName,
    platform,
    REASON_TO_MESSAGE_KEY[reasonForExpansion]
  );
}

/** Desktop-only: the exact-match count line shown above the grid for Low Result. */
export function getLowResultExactCountMessage(searchMessaging, storeName, platform = 'desktop') {
  return resolveMessage(searchMessaging, storeName, platform, 'lowResultExactCount');
}

// Spell-correction "search anyway" link target: current URL with spellCheckEnable=false,
// so re-searching skips spell correction and returns the user's original query.
function buildSpellCheckDisabledHref() {
  if (typeof window === 'undefined' || !window.location) {
    return '?spellCheckEnable=false';
  }
  const url = new URL(window.location.href);
  url.searchParams.set('spellCheckEnable', 'false');
  return `${url.pathname}${url.search}${url.hash}`;
}

// Replaces {token} placeholders in a plain-text string with their values. Missing tokens
// are dropped so the sentence still reads.
function interpolate(text, tokens) {
  return text
    .split(/\{(\w+)\}/g)
    .map((part, index) => {
      if (index % 2 === 0) {
        return part;
      }
      const value = tokens[part];
      return value == null ? '' : value;
    })
    .join('');
}

const MARKUP_TAG = /<(strong|link)>(.*?)<\/\1>/g;

// Renders one line of a template: splits out <strong>/<link> markup, interpolates
// {token} placeholders inside each part, and wraps markup parts in the matching element.
function renderLine(line, tokens) {
  const nodes = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  MARKUP_TAG.lastIndex = 0;
  while ((match = MARKUP_TAG.exec(line))) {
    if (match.index > lastIndex) {
      nodes.push(interpolate(line.slice(lastIndex, match.index), tokens));
    }
    const [, tag, inner] = match;
    const text = interpolate(inner, tokens);
    key += 1;
    if (tag === 'strong') {
      nodes.push(<strong key={key}>{text}</strong>);
    } else {
      nodes.push(
        <a className="search-message-link" href={buildSpellCheckDisabledHref()} key={key}>
          {text}
        </a>
      );
    }
    lastIndex = MARKUP_TAG.lastIndex;
  }
  if (lastIndex < line.length) {
    nodes.push(interpolate(line.slice(lastIndex), tokens));
  }
  return nodes;
}

// Splits a template on '\n' and renders each line. Multi-line templates get positional
// classes (--first/--second) so mSite can size the first line smaller than the second;
// single-line templates keep the banner's default size.
function renderMessage(template, tokens) {
  const lines = String(template).split('\n');
  const isMultiLine = lines.length > 1;

  return lines.map((line, lineIndex) => {
    let positionClass = '';
    if (isMultiLine) {
      positionClass =
        lineIndex === 0 ? ' search-message-line--first' : ' search-message-line--second';
    }
    return (
      <div className={`search-message-line${positionClass}`} key={`ln-${lineIndex}`}>
        {renderLine(line, tokens)}
      </div>
    );
  });
}

/**
 * Search messaging banner. Renders the interpolated message in one of four variants.
 *   message — copy template from getSearchMessage/getLowResultExactCountMessage
 *   tokens  — values interpolated into the template (e.g. { freeTextSearch, correctedQuery })
 *   theme   — store skin: 'ajio' | 'luxe' | 'ajiogram' (strip variant only)
 *   variant — 'strip'   themed strip above the grid (ZSR / spell correction)
 *             'lowResult' mSite in-grid low-result divider (white strip in product grid)
 *             'divider' full-width in-grid section heading (desktop low-result divider)
 *             'inline'  short line above the grid (desktop low-result exact-match count)
 */
const SearchMessageBanner = ({ message, tokens = {}, theme = '', variant = 'strip' }) => {
  if (!message) {
    return null;
  }
  const content = renderMessage(message, tokens);
  if (variant === 'divider') {
    return (
      <div className="low-result-divider" role="status">
        {content}
      </div>
    );
  }
  if (variant === 'inline') {
    return (
      <div className="low-result-exact-count" role="status">
        {content}
      </div>
    );
  }
  if (variant === 'lowResult') {
    const themeClass = theme ? `search-message-banner--${theme}` : '';

    return (
      <div
        className={`search-message-banner search-message-banner--low-result ${themeClass}`}
        role="status"
      >
        {content}
      </div>
    );
  }
  const themeClass = theme ? `search-message-banner--${theme}` : '';
  return (
    <div
      className={`search-message-banner search-message-banner--strip ${themeClass}`}
      role="status"
    >
      {content}
    </div>
  );
};

export default SearchMessageBanner;
